import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../app.config';
import { DataService } from '../../core/service/base/data.service';
import { MessageService } from '../../core/service/message.service';
import { ProgressBarService } from '../../core/service/progress-bar.service';
import { LettingAdvertisment } from '../model/letting-advertisment';
import { DhMarketOptions, HomeqOptions } from '../model/platform-options';
import { LettingShowing } from '../model/letting-showing';

@Injectable()
export class LettingAdvertismentService extends DataService<LettingAdvertisment> {
  constructor(client: HttpClient, @Inject(APP_CONFIG) config: IAppConfig, messageService: MessageService, progressBar: ProgressBarService) {
    super(client, config, messageService, LettingAdvertisment, progressBar, 'letting-advertisment');
  }

  public getRentalObjectAdvertisments(rentalObjectId: string): Observable<LettingAdvertisment[]> {
    return this.client
      .get<LettingAdvertisment[]>(`${this.endpoint}/rental-object/${rentalObjectId}`)
      .pipe(map(res => res.map(adv => this.createModel(adv))));
  }

  public getRentalObjectTentants(rentalObjectId: string): Observable<LettingShowing[]> {
    return this.client
      .get<LettingShowing[]>(`${this.endpoint}/rental-object/${rentalObjectId}/tenants`)
      .pipe(map(res => res.map(item => new LettingShowing(item))));
  }

  public getDefaultsForRentalObject(rentalObjectId: string): Observable<LettingAdvertisment> {
    return this.client.get<LettingAdvertisment>(`${this.endpoint}/defaults/${rentalObjectId}`).pipe(map(res => this.createModel(res)));
  }

  sendToPlatform(id: string, officeId?: string): Observable<any> {
    this.progressBar.start();
    return this.client.post<any>(`${this.endpoint}/send`, { LettingAdvertisementId: id, companyId: officeId }).pipe(
      tap(() => this.notification.showSuccess('LETTING.ADVERTISMENT.SENT_TO_PLATFORM')),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  unpublishFromBoplatsSverige(id: string): Observable<string> {
    this.progressBar.start();
    return this.client.post<string>(`${this.endpoint}/${id}/unpublish`, {}).pipe(
      tap(() => this.notification.showSuccess('LETTING.ADVERTISMENT.UNPUBLISHED_FROM_PLATFORM')),
      catchError(error => {
        this.notification.showError(error);
        throw error;
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  change(advert: LettingAdvertisment): Observable<any> {
    this.progressBar.start();
    return this.client.put(this.endpoint, JSON.stringify(advert)).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  add(advert: LettingAdvertisment): Observable<string> {
    this.progressBar.start();
    return this.client.post<string>(this.endpoint, JSON.stringify(advert)).pipe(
      tap(() => this.notification.showSuccess()),
      finalize(() => this.progressBar.stop())
    );
  }

  getHomeqOptions(): Observable<HomeqOptions> {
    return this.client.get<HomeqOptions>(`${this.endpoint}/homeq-options`).pipe(map(val => new HomeqOptions(val)));
  }

  getDhMarketOptions(): Observable<DhMarketOptions> {
    return this.client.get<DhMarketOptions>(`${this.endpoint}/dhmarket-options`).pipe(map(val => new DhMarketOptions(val)));
  }

  protected createModel(modelData: any): LettingAdvertisment {
    return new LettingAdvertisment(modelData);
  }
}
